/* VEXLARI motion layer for Dawn. No dependencies.
   Sections opt in with data-vx="<name>"; each init returns a cleanup so the theme editor can reload sections. */
(() => {
  const root = document.documentElement;
  const MOTION = root.classList.contains('vx-motion');
  const FINE = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* ---------- one frame loop for everything ---------- */
  let sy = scrollY, lastSy = sy, vel = 0, lastT = performance.now();
  const frameFns = new Set(), scrollFns = new Set();
  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  addEventListener('pointermove', e => { mouse.tx = e.clientX / innerWidth * 2 - 1; mouse.ty = e.clientY / innerHeight * 2 - 1; }, { passive: true });
  /* phones: tilt comes from a finger on the hero or from the gyroscope (no permission prompt; browsers that need one just skip it) */
  const tilt = { x: 0, y: 0, tx: 0, ty: 0, touching: false };
  let beta0 = null;
  if (!FINE && MOTION) addEventListener('deviceorientation', e => {
    if (e.gamma == null || tilt.touching) return;
    if (beta0 === null) beta0 = e.beta;
    tilt.tx = clamp(e.gamma / 28, -1, 1); tilt.ty = clamp((e.beta - beta0) / 28, -1, 1);
  }, { passive: true });
  function loop(t) {
    const dt = Math.min(64, t - lastT) || 16; lastT = t;
    sy = scrollY;
    const dy = sy - lastSy;
    vel = lerp(vel, dy / dt * 16, .18);
    mouse.x = lerp(mouse.x, mouse.tx, .06); mouse.y = lerp(mouse.y, mouse.ty, .06);
    tilt.x = lerp(tilt.x, tilt.tx, .08); tilt.y = lerp(tilt.y, tilt.ty, .08);
    document.body.classList.toggle('vx-top', sy < 40);
    if (dy !== 0) scrollFns.forEach(f => f(sy));
    frameFns.forEach(f => f(dt));
    lastSy = sy;
    requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);

  /* header height drives the hero overlap and sticky offsets */
  const setHdr = () => { const h = $('.section-header'); if (h) root.style.setProperty('--vx-hdr-h', `${Math.round(h.getBoundingClientRect().height)}px`); };
  setHdr(); addEventListener('resize', setHdr); document.fonts?.ready.then(setHdr);

  /* ---------- helpers ---------- */
  function fitWord(el, weight = 900, pad = 1) {
    if (!el) return;
    const m = document.createElement('span');
    m.textContent = el.textContent.trim();
    m.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;font-family:var(--vx-display);font-weight:${weight};font-stretch:125%;font-size:100px;letter-spacing:0`;
    document.body.appendChild(m);
    const w = m.getBoundingClientRect().width; m.remove();
    const cs = getComputedStyle(el);
    const avail = el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    if (w > 0 && avail > 0) el.style.fontSize = `${Math.floor(100 * avail / w * pad * 10) / 10}px`;
  }
  function splitLines(scope) {
    $$('[data-vx-split]', scope).forEach(el => {
      if (el.dataset.split) return; el.dataset.split = '1';
      el.innerHTML = el.innerHTML.split(/<br\s*\/?>/i).map((h, i) => `<span class="ln"><span style="--d:${i * 0.09}s">${h.trim()}</span></span>`).join('');
    });
  }
  function reveals(scope) {
    if (!MOTION) return () => {};
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px', threshold: .12 });
    $$('[data-vx-reveal], [data-vx-split]', scope).forEach(el => io.observe(el));
    return () => io.disconnect();
  }
  function magnets(scope) {
    if (!FINE || !MOTION) return;
    $$('[data-vx-magnet]', scope).forEach(b => {
      b.addEventListener('pointermove', e => { const r = b.getBoundingClientRect(); b.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * .18}px, ${(e.clientY - r.top - r.height / 2) * .3}px)`; });
      b.addEventListener('pointerleave', () => { b.style.transform = ''; });
    });
  }

  /* ---------- sections ---------- */
  const inits = {
    hero(el) {
      const word = $('[data-vx-word]', el), vis = $('.vx-hero__visual', el), stack = $('.vx-hero__stack', el);
      const teeC = $('[data-vx-tee]', el), img = $('img', teeC), moon = $('.vx-hero__moon', el);
      const side = $$('.vx-hero__tee--l, .vx-hero__tee--r', el), chars = $$('.vx-ch > span', word);
      let narrow = innerWidth < 700;
      const fit = () => {
        fitWord(word, 900, .995);
        const ar = (img?.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : (+img?.getAttribute('width') / +img?.getAttribute('height')) || 1.08;
        const wide = innerWidth >= 700; narrow = !wide;
        const availH = (vis.clientHeight - 24) * (wide ? .86 : 1), availW = stack.clientWidth * (wide ? .5 : .84);
        stack.style.setProperty('--tw', `${Math.max(160, Math.min(availW, availH * ar, 760))}px`);
      };
      fit(); document.fonts?.ready.then(fit); img?.addEventListener('load', fit);
      addEventListener('resize', fit);
      // countdown
      const cd = $('[data-vx-countdown]', el), cta = $('[data-cd-cta]', el), btn = $('.vx-hero__cta .vx-btn', el);
      const launch = el.dataset.launch ? new Date(el.dataset.launch) : null;
      const pad = n => String(n).padStart(2, '0');
      // phones: a floating bar with the countdown and the shop button once the hero is scrolled past
      const bar = document.createElement('a');
      bar.className = 'vx-sticky'; bar.href = btn?.getAttribute('href') || '/collections/all';
      bar.innerHTML = '<span class="vx-sticky__l"><span class="vx-dot"></span><span data-st-label></span></span><span class="vx-sticky__r"><span data-st-cta></span><svg class="vx-arr" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M2 8h11M9 4l4 4-4 4"/></svg></span>';
      if (!window.Shopify?.designMode) document.body.appendChild(bar);
      const stLabel = $('[data-st-label]', bar), stCta = $('[data-st-cta]', bar);
      const setU = (k, v) => {
        const b = $(`[data-cd="${k}"]`, cd); if (!b || b.textContent === v) return;
        b.textContent = v;
        if (MOTION) { b.classList.remove('vx-tick'); void b.offsetWidth; b.classList.add('vx-tick'); }
      };
      const tick = () => {
        const label = $('[data-cd-label]', cd || bar);
        if (!launch || isNaN(launch)) { if (cd) cd.hidden = true; stLabel.textContent = el.getAttribute('aria-label') || ''; stCta.textContent = cta?.textContent || 'Shop'; return; }
        const ms = launch - Date.now(), live = ms <= 0;
        const ctaText = live ? el.dataset.liveCta : (el.dataset.preCta || el.dataset.liveCta);
        if (cta) cta.textContent = ctaText;
        stCta.textContent = ctaText; bar.classList.toggle('is-live', live);
        if (live) {
          stLabel.textContent = el.dataset.liveLabel;
          if (cd) { cd.classList.add('is-live'); label.textContent = el.dataset.liveLabel; $('.vx-units', cd).hidden = true; }
          return;
        }
        const d = pad(Math.floor(ms / 864e5)), h = pad(Math.floor(ms / 36e5) % 24), m = pad(Math.floor(ms / 6e4) % 60), s = pad(Math.floor(ms / 1e3) % 60);
        stLabel.textContent = `${d}d ${h}h ${m}m ${s}s`;
        if (!cd) return;
        setU('d', d); setU('h', h); setU('m', m); setU('s', s);
      };
      const stick = () => {
        const past = scrollY > el.offsetHeight * .75, nearEnd = innerHeight + scrollY > document.documentElement.scrollHeight - 320;
        bar.classList.toggle('on', past && !nearEnd);
      };
      stick(); scrollFns.add(stick);
      // touch: drag a finger over the garments to tilt them
      const setTouch = e => { const r = vis.getBoundingClientRect(); tilt.tx = clamp((e.clientX - r.left) / r.width * 2 - 1, -1, 1); tilt.ty = clamp((e.clientY - r.top) / r.height * 2 - 1, -1, 1); };
      const tDown = e => { if (e.pointerType !== 'touch') return; tilt.touching = true; setTouch(e); };
      const tMove = e => { if (tilt.touching) setTouch(e); };
      const tUp = () => { if (!tilt.touching) return; tilt.touching = false; tilt.tx = 0; tilt.ty = 0; };
      if (MOTION && !FINE) { vis.addEventListener('pointerdown', tDown); vis.addEventListener('pointermove', tMove, { passive: true }); ['pointerup', 'pointercancel', 'pointerleave'].forEach(n => vis.addEventListener(n, tUp)); }
      tick(); const iv = setInterval(tick, 1000);
      // scroll + pointer parallax
      let introDone = false;
      const frame = () => {
        const h = el.offsetHeight, p = clamp(scrollY / h);
        if (p >= 1) return;
        const mx = FINE ? mouse.x : tilt.x, my = FINE ? mouse.y : tilt.y, k = FINE ? 1 : 1.6;
        if (moon) { moon.style.setProperty('--mx', `${-mx * 18 * k}px`); moon.style.setProperty('--my', `${p * h * .35 - my * 12 * k}px`); }
        teeC.style.transform = `translateX(-50%) translateY(${-p * h * .12}px) rotateY(${mx * 7 * k}deg) rotateX(${-my * 4 * k}deg) scale(${1 - p * .1})`;
        // phones keep the side garments tucked behind the centre one and fan them out as you scroll
        const bl = narrow ? -108 : -135, br = narrow ? 8 : 35, sp = narrow ? 46 : 0, rot = narrow ? 14 : 6;
        if (side[0]) side[0].style.transform = `translateX(calc(${bl - p * sp}% + ${-mx * 10 * k}px)) translateY(calc(-8% + ${-p * h * .2}px)) rotate(${-7 - p * rot + mx * 3 * k}deg)`;
        if (side[1]) side[1].style.transform = `translateX(calc(${br + p * sp}% + ${-mx * 10 * k}px)) translateY(calc(-8% + ${-p * h * .2}px)) rotate(${7 + p * rot + mx * 3 * k}deg)`;
        if (root.classList.contains('vx-pre')) return;
        if (!introDone) { introDone = true; setTimeout(() => chars.forEach(c => { c.style.transition = 'none'; }), 2400); }
        chars.forEach((c, i) => { c.style.transform = `translateX(${(i - (chars.length - 1) / 2) * p * 22}px) translateY(${p * 30}px)`; });
      };
      if (MOTION) frameFns.add(frame);
      return () => {
        clearInterval(iv); frameFns.delete(frame); scrollFns.delete(stick); removeEventListener('resize', fit); bar.remove();
        vis.removeEventListener('pointerdown', tDown); vis.removeEventListener('pointermove', tMove);
      };
    },

    marquee(el) {
      const mq = $('[data-vx-mq]', el), base = (+el.dataset.speed || 45) / 1000;
      let x = 0, dir = -1, skew = 0, half = mq.scrollWidth / 2;
      const measure = () => { half = mq.scrollWidth / 2; };
      document.fonts?.ready.then(measure); addEventListener('resize', measure);
      const frame = dt => {
        if (!half) return;
        if (Math.abs(vel) > .5) dir = vel > 0 ? -1 : 1;
        const speed = MOTION ? base + Math.min(Math.abs(vel) * .012, .5) : base * .6;
        x += dir * speed * dt;
        if (x <= -half) x += half; if (x > 0) x -= half;
        skew = lerp(skew, MOTION ? clamp(vel * .35, -10, 10) : 0, .12);
        mq.style.transform = `translate3d(${x}px,0,0) skewX(${skew}deg)`;
      };
      frameFns.add(frame);
      return () => { frameFns.delete(frame); removeEventListener('resize', measure); };
    },

    lineup(el) {
      const track = $('[data-vx-lu-track]', el), bar = $('[data-vx-lu-p]', el), num = $('[data-vx-lu-n]', el);
      const total = +num.dataset.total || track.children.length;
      const p2 = n => String(n).padStart(2, '0');
      const setNum = p => { num.textContent = `${p2(Math.min(total, Math.floor(p * total) + 1))} / ${p2(total)}`; bar.style.setProperty('--p', p); };
      let pinned = false, dist = 0;
      const layout = () => {
        pinned = MOTION && innerWidth >= 1080 && !window.Shopify?.designMode;
        el.classList.toggle('pinned', pinned);
        if (pinned) { dist = Math.max(0, track.scrollWidth - innerWidth); el.style.height = `${innerHeight + dist}px`; }
        else { el.style.height = ''; track.style.transform = ''; }
        onScroll();
      };
      const onScroll = () => {
        if (!pinned) return;
        const top = el.getBoundingClientRect().top + scrollY;
        const p = clamp((scrollY - top) / Math.max(1, el.offsetHeight - innerHeight));
        track.style.transform = `translate3d(${-p * dist}px,0,0)`;
        setNum(p);
      };
      const cards = $$('.vx-lu', track);
      const flow = () => {
        if (pinned || !MOTION) return;
        const tr = track.getBoundingClientRect(), c = tr.left + tr.width / 2;
        cards.forEach(cd => { const r = cd.getBoundingClientRect(); const k = clamp((r.left + r.width / 2 - c) / (tr.width * .75), -1, 1); cd.style.setProperty('--k', k.toFixed(3)); cd.style.setProperty('--a', Math.abs(k).toFixed(3)); });
      };
      const onTrack = () => { if (!pinned) { setNum(clamp(track.scrollLeft / Math.max(1, track.scrollWidth - track.clientWidth))); flow(); } };
      layout(); flow(); addEventListener('resize', layout); document.fonts?.ready.then(() => { layout(); flow(); });
      scrollFns.add(onScroll); track.addEventListener('scroll', onTrack, { passive: true });
      let hinted = false;
      const hint = new IntersectionObserver(es => es.forEach(e => {
        if (!e.isIntersecting || hinted || pinned || !MOTION || FINE) return;
        hinted = true; hint.disconnect();
        track.scrollBy({ left: 110, behavior: 'smooth' }); setTimeout(() => track.scrollBy({ left: -110, behavior: 'smooth' }), 650);
      }), { threshold: .6 });
      hint.observe(track);
      return () => { scrollFns.delete(onScroll); removeEventListener('resize', layout); el.style.height = ''; hint.disconnect(); };
    },

    craft(el) {
      const frame = $('[data-vx-craft-frame]', el); if (!frame) return;
      const imgs = $$('img', frame), steps = $$('.vx-step', el); let cur = 0;
      const rv = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); rv.unobserve(e.target); } }), { rootMargin: '0px 0px -12% 0px', threshold: .15 });
      if (MOTION) steps.forEach(s => rv.observe(s)); else steps.forEach(s => s.classList.add('in'));
      const io = new IntersectionObserver(es => es.forEach(e => {
        if (!e.isIntersecting) return;
        const i = +e.target.dataset.step; if (i === cur) return;
        steps.forEach(s => s.classList.toggle('on', s === e.target));
        imgs.forEach((im, j) => { im.classList.toggle('was', j === cur); im.classList.toggle('on', j === i); im.style.zIndex = j === i ? 2 : j === cur ? 1 : 0; });
        cur = i;
      }), { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach(s => io.observe(s));
      return () => { io.disconnect(); rv.disconnect(); };
    },

    story(el) {
      const art = $('[data-vx-ns-art]', el);
      if (!MOTION) { art.style.setProperty('--p', 1); return; }
      const f = () => { const r = el.getBoundingClientRect(); art.style.setProperty('--p', clamp((-r.top + innerHeight * .15) / Math.max(1, el.offsetHeight - innerHeight * 1.1)).toFixed(4)); };
      f(); scrollFns.add(f);
      return () => scrollFns.delete(f);
    },

    about(el) {
      const moon = $('[data-vx-about-moon]', el);
      const f = () => { if (!moon) return; const r = el.getBoundingClientRect(); moon.style.setProperty('--my', `${Math.max(0, -r.top) * .35}px`); };
      if (MOTION) { f(); scrollFns.add(f); }
      // stats count up when they arrive
      const nums = $$('[data-vx-count]', el);
      if (MOTION) nums.forEach(n => { n.textContent = '0'; });
      const io = new IntersectionObserver(es => es.forEach(e => {
        if (!e.isIntersecting) return; io.unobserve(e.target);
        const b = e.target, end = parseInt(b.dataset.vxCount, 10); if (!MOTION || isNaN(end)) return;
        const t0 = performance.now(), dur = 1400;
        const step = t => { const k = clamp((t - t0) / dur); b.textContent = String(Math.round(end * (1 - Math.pow(1 - k, 3)))); if (k < 1) requestAnimationFrame(step); };
        requestAnimationFrame(step);
      }), { threshold: .5 });
      nums.forEach(n => io.observe(n));
      return () => { scrollFns.delete(f); io.disconnect(); };
    },

    wordmark(el) {
      const fw = $('[data-vx-fw]', el);
      const fit = () => fitWord(fw, 900, .97);
      fit(); document.fonts?.ready.then(fit); addEventListener('resize', fit);
      if (FINE && MOTION) {
        const spans = $$('span', fw);
        fw.addEventListener('pointermove', e => spans.forEach(s => { const r = s.getBoundingClientRect(); const d = Math.abs(e.clientX - (r.left + r.width / 2)); s.style.fontWeight = Math.round(900 - clamp(d / 380) * 560); s.style.color = d < 90 ? 'var(--vx-moon)' : ''; }));
        fw.addEventListener('pointerleave', () => spans.forEach(s => { s.style.fontWeight = ''; s.style.color = ''; }));
      }
      return () => removeEventListener('resize', fit);
    }
  };

  const live = new Map();
  function initSection(el) {
    const name = el.dataset.vx;
    if (!inits[name] || live.has(el)) return;
    splitLines(el);
    const offReveal = reveals(el);
    magnets(el);
    const off = inits[name](el) || (() => {});
    live.set(el, () => { off(); offReveal(); });
  }
  function initAll(scope = document) { $$('[data-vx]', scope).forEach(initSection); }
  function destroyIn(scope) { [...live.keys()].forEach(el => { if (scope.contains(el)) { live.get(el)(); live.delete(el); } }); }

  if (MOTION) {
    const pr = document.createElement('div'); pr.className = 'vx-progress'; pr.setAttribute('aria-hidden', 'true'); document.body.appendChild(pr);
    const f = () => { pr.style.transform = `scaleX(${clamp(scrollY / Math.max(1, document.documentElement.scrollHeight - innerHeight)).toFixed(4)})`; };
    f(); scrollFns.add(f); addEventListener('resize', f);
  }

  initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));
  document.addEventListener('shopify:section:unload', e => destroyIn(e.target));

  /* ---------- cursor bubble on product media ---------- */
  if (FINE && MOTION) {
    const cur = document.createElement('div'); cur.className = 'vx-cur'; cur.setAttribute('aria-hidden', 'true'); cur.textContent = 'View';
    document.body.appendChild(cur);
    let cx = -200, cy = -200, tx = -200, ty = -200;
    addEventListener('pointermove', e => { tx = e.clientX; ty = e.clientY; cur.classList.toggle('on', !!e.target.closest?.('[data-vx-cursor], .card-wrapper .card__media')); }, { passive: true });
    frameFns.add(() => { cx = lerp(cx, tx, .2); cy = lerp(cy, ty, .2); cur.style.setProperty('--x', `${cx}px`); cur.style.setProperty('--y', `${cy}px`); });
  }

  /* ---------- first-visit preloader (homepage only) ---------- */
  const loader = $('.vx-loader');
  if (root.classList.contains('vx-first') && loader) {
    const n = $('[data-vx-ldc]', loader), bar = $('.vx-loader__bar', loader), t0 = performance.now();
    let ready = false;
    Promise.race([Promise.all([document.fonts?.ready, new Promise(r => (document.readyState === 'complete' ? r() : addEventListener('load', r, { once: true })))]), new Promise(r => setTimeout(r, 2000))]).then(() => { ready = true; });
    const step = t => {
      const k = clamp((t - t0) / 1100), shown = ready ? k : Math.min(k, .9);
      n.textContent = String(Math.round(shown * 100)).padStart(3, '0'); bar.style.transform = `scaleX(${shown})`;
      if (shown >= 1) {
        loader.classList.add('done');
        setTimeout(() => root.classList.remove('vx-pre'), 380);
        setTimeout(() => root.classList.remove('vx-first'), 1300);
      } else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  } else if (root.classList.contains('vx-pre')) {
    (document.fonts?.ready || Promise.resolve()).then(() => requestAnimationFrame(() => root.classList.remove('vx-pre')));
  }
})();
